import { captureEnvironment, newRunId, type EnvironmentInfo } from './environment';
import type { RunStatus } from './cloudflare-run';

const VENDOR_DIR = 'build/vendor';

interface MlabDirection {
  meanClientMbps: number | null;
  elapsedSeconds: number | null;
  numBytes: number | null;
  serverMinRttMs: number | null;
  serverRttMs: number | null;
  serverBytesSent: number | null;
  serverBytesRetrans: number | null;
}

export interface MlabRun {
  schema: 'measurement-spike/v1';
  kind: 'mlab-ndt7';
  id: string;
  label: string;
  startedAt: string;
  environment: EnvironmentInfo;
  client: { name: '@m-lab/ndt7'; version: string };
  status: RunStatus;
  wallClockMs: number | null;
  returnCode: number | null;
  server: { machine: string | null; city: string | null; country: string | null } | null;
  download: MlabDirection | null;
  upload: MlabDirection | null;
  errors: Array<{ atMs: number; message: string }>;
}

let scriptLoad: Promise<void> | undefined;

function loadNdt7(): Promise<void> {
  scriptLoad ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${VENDOR_DIR}/ndt7.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load ndt7.js'));
    document.head.append(script);
  });
  return scriptLoad;
}

const usToMs = (us: number | undefined): number | null => (us === undefined ? null : us / 1000);

function toDirection(data: Ndt7Complete): MlabDirection | null {
  const client = data.LastClientMeasurement;
  const tcp = data.LastServerMeasurement?.TCPInfo;
  // ndt7-js reports "complete" with no measurements when its 10 s connection timeout fires.
  if (!client && !tcp) return null;
  return {
    meanClientMbps: client?.MeanClientMbps ?? null,
    elapsedSeconds: client?.ElapsedTime ?? null,
    numBytes: client?.NumBytes ?? null,
    serverMinRttMs: usToMs(tcp?.MinRTT),
    serverRttMs: usToMs(tcp?.RTT),
    serverBytesSent: tcp?.BytesSent ?? null,
    serverBytesRetrans: tcp?.BytesRetrans ?? null,
  };
}

/** Runs only after the caller has obtained explicit consent to M-Lab's public data policy. */
export async function runMlabReference(label: string, userAcceptedDataPolicy: true): Promise<MlabRun> {
  const t0 = performance.now();
  const run: MlabRun = {
    schema: 'measurement-spike/v1',
    kind: 'mlab-ndt7',
    id: newRunId(),
    label,
    startedAt: new Date().toISOString(),
    environment: captureEnvironment(),
    client: { name: '@m-lab/ndt7', version: __NDT7_VERSION__ },
    status: 'running',
    wallClockMs: null,
    returnCode: null,
    server: null,
    download: null,
    upload: null,
    errors: [],
  };
  const addError = (message: unknown) => run.errors.push({ atMs: performance.now() - t0, message: String(message) });

  try {
    await loadNdt7();
    const ndt7 = window.ndt7;
    if (!ndt7) throw new Error('ndt7 global not found after loading script');
    run.returnCode = await ndt7.test(
      {
        userAcceptedDataPolicy,
        downloadworkerfile: `${VENDOR_DIR}/ndt7-download-worker.js`,
        uploadworkerfile: `${VENDOR_DIR}/ndt7-upload-worker.js`,
        metadata: { client_name: 'internet-doctor-measurement-spike', client_version: '0.0.0' },
      },
      {
        error: addError,
        // Only the server identity is kept; the locate result's URLs carry access tokens.
        serverChosen: (server) => {
          run.server = {
            machine: server.machine ?? null,
            city: server.location?.city ?? null,
            country: server.location?.country ?? null,
          };
        },
        downloadComplete: (data) => {
          run.download = toDirection(data);
        },
        uploadComplete: (data) => {
          run.upload = toDirection(data);
        },
      },
    );
  } catch (error) {
    addError(error);
  }

  run.wallClockMs = performance.now() - t0;
  run.status = run.returnCode === 0 && run.errors.length === 0 && run.download && run.upload ? 'finished' : 'error';
  return run;
}
